import type { ReactNode } from 'react';
import {
  IconAlertTriangle,
  IconBulb,
  IconCircleCheck,
  IconHandStop,
  IconInfoCircle,
  IconListCheck,
} from '@tabler/icons-react';
import { PROCEDURES_CALLOUT_TYPE, type ProcedureCalloutView } from '@actschurch/ccms-sdk';

/**
 * Callouts styled by calloutTypeID: Tip blue, Check green, Careful orange, Warning red, Done teal.
 * The lookup's icon (Material Symbols names in the seed: lightbulb, fact_check, front_hand, warning,
 * task_alt) is mapped to the matching Tabler icon; the type id decides when the name is unknown.
 * Colours come from styles/procedures.css (AA in both schemes), never colour alone: the type name is
 * always written out.
 */
const ICON_BY_NAME: Record<string, ReactNode> = {
  lightbulb: <IconBulb size={22} />,
  tips_and_updates: <IconBulb size={22} />,
  fact_check: <IconListCheck size={22} />,
  checklist: <IconListCheck size={22} />,
  front_hand: <IconHandStop size={22} />,
  pan_tool: <IconHandStop size={22} />,
  warning: <IconAlertTriangle size={22} />,
  report: <IconAlertTriangle size={22} />,
  task_alt: <IconCircleCheck size={22} />,
  check_circle: <IconCircleCheck size={22} />,
};

const ICON_BY_TYPE: Record<number, ReactNode> = {
  [PROCEDURES_CALLOUT_TYPE.Tip]: <IconBulb size={22} />,
  [PROCEDURES_CALLOUT_TYPE.Check]: <IconListCheck size={22} />,
  [PROCEDURES_CALLOUT_TYPE.Careful]: <IconHandStop size={22} />,
  [PROCEDURES_CALLOUT_TYPE.Warning]: <IconAlertTriangle size={22} />,
  [PROCEDURES_CALLOUT_TYPE.Done]: <IconCircleCheck size={22} />,
};

const KIND_BY_TYPE: Record<number, string> = {
  [PROCEDURES_CALLOUT_TYPE.Tip]: 'tip',
  [PROCEDURES_CALLOUT_TYPE.Check]: 'check',
  [PROCEDURES_CALLOUT_TYPE.Careful]: 'careful',
  [PROCEDURES_CALLOUT_TYPE.Warning]: 'warning',
  [PROCEDURES_CALLOUT_TYPE.Done]: 'done',
};

const LABEL_BY_TYPE: Record<number, string> = {
  [PROCEDURES_CALLOUT_TYPE.Tip]: 'Tip',
  [PROCEDURES_CALLOUT_TYPE.Check]: 'Check',
  [PROCEDURES_CALLOUT_TYPE.Careful]: 'Careful',
  [PROCEDURES_CALLOUT_TYPE.Warning]: 'Warning',
  [PROCEDURES_CALLOUT_TYPE.Done]: 'Done',
};

function iconFor(c: ProcedureCalloutView): ReactNode {
  const name = (c.calloutTypeIcon ?? '').trim().toLowerCase();
  return ICON_BY_NAME[name] ?? ICON_BY_TYPE[c.calloutTypeID] ?? <IconInfoCircle size={22} />;
}

export function ProcedureCallout({ callout }: { callout: ProcedureCalloutView }) {
  const kind = KIND_BY_TYPE[callout.calloutTypeID] ?? 'tip';
  const label = callout.calloutTypeName || LABEL_BY_TYPE[callout.calloutTypeID] || 'Note';
  return (
    <div className={`proc-pcallout ${kind}`} role={kind === 'warning' ? 'note' : undefined}>
      <span className="ic" aria-hidden="true">{iconFor(callout)}</span>
      <div className="body">
        <span className="label">{label}</span>
        <span className="text">{callout.text}</span>
      </div>
    </div>
  );
}

/** A list of callouts in their sort order (warnings first is the editor's call, not ours). */
export function CalloutList({ callouts }: { callouts: ProcedureCalloutView[] }) {
  if (!callouts.length) return null;
  const sorted = [...callouts].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <div className="proc-pcallouts">
      {sorted.map((c) => <ProcedureCallout key={c.calloutID} callout={c} />)}
    </div>
  );
}
